import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Breakpoints, DefaultDateFormatter, useBreakpoint } from '../../lib';
import { ChildrenProp, ClassNameProp } from '../common-types';

/**
 * Properties for an {@link DocumentGroupList} component.
 */
export interface DocumentGroupListProps
  extends Required<ChildrenProp>,
    ClassNameProp {
  /**
   * ColorId of the encounter (0 = no color).
   */
  colorId: 0 | 1 | 2 | 3 | 4 | 5 | 6;

  /**
   * The period in which the encounter happened.
   */
  period: {
    /**
     * Start time of the period.
     */
    start: Date;
    /**
     * End time of the period.
     */
    end: Date;
  };

  /**
   * Encounter institution.
   */
  serviceProvider: {
    /**
     * Identifier of the institution.
     */
    identifier: string;
    /**
     * Name of the institution.
     */
    name: string;
  };

  /**
   * If the institution should be displayed or not.
   */
  showInstitution: boolean;

  /**
   * Total number of documents in this encounter.
   * If no value was passed it will be calculated by the component.
   * This can be useful for server-side paging.
   */
  totalDocuments: number;
  /**
   * If the encounter should be automatically scrolled to.
   */
  scrollIntoView?: boolean;
}

/**
 * Formats 2 dates and in case they are equal, prints just one date.
 *
 * @param start encounter start date
 * @param end encounter end date
 */
function dateRangeFormat(start: Date, end: Date) {
  const startDate = DefaultDateFormatter.formatDate(start);
  const endDate = DefaultDateFormatter.formatDate(end);

  return [startDate, startDate !== endDate && endDate]
    .filter(Boolean)
    .join(' - ');
}

/**
 * Component used for displaying the Documents (which might be linked or not) inside of an encounter.
 *
 * @param props see {@link DocumentGroupListProps}
 * @return the created {@link JSX.Element}
 * @constructor
 */
export const DocumentGroupList = React.memo(function DocumentGroupList(
  props: DocumentGroupListProps
): JSX.Element {
  const mobile = useBreakpoint(Breakpoints.MOBILE);
  const ref = useRef<HTMLDivElement>(null);

  // On mobile, we want to hide all the institution stuff to save space
  const colorId = mobile ? 0 : props.colorId;
  const showInstitution = !mobile && props.showInstitution;

  // the institution that will be displayed
  let institutionElement = <></>;
  if (showInstitution && colorId !== 0) {
    // create the institution element
    institutionElement = (
      <div className={'encounter-documents-institution'}>
        <span className={'big'}>
          {props.serviceProvider.name}
          <span className={'amount'}> ({props.totalDocuments})</span>
        </span>
        <span className={'small'}>
          {dateRangeFormat(props.period.start, props.period.end)}
        </span>
      </div>
    );
  } else if (showInstitution && colorId === 0) {
    institutionElement = <div className={'encounter-documents-institution'} />;
  }

  useLayoutEffect(() => {
    if (props.scrollIntoView) {
      ref.current?.scrollIntoView();
    }
    // it should only ever happen exactly once on render.
    // because of this, the dependencies are intentionally kept empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const className = useMemo(
    () => `ehs-encounter-documents-container ${props.className || ''}`.trim(),
    [props.className]
  );
  // return encounter container
  return (
    <div className={className} role="article" ref={ref}>
      <div className={`ehs-encounter-documents color${colorId}`}>
        {props.children}
      </div>
      {institutionElement}
    </div>
  );
});
